import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, CheckCircle2, Loader2, XCircle } from "lucide-react";
import API from "../api/axiosConfig";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const passwordStrength = () => {
    if (password.length === 0) return null;
    if (password.length < 6) return "weak";
    if (password.length < 10 || !/[A-Z]/.test(password) || !/\d/.test(password)) return "medium";
    return "strong";
  };

  const strength = passwordStrength();
  const strengthLabel = { weak: "Muy corta", medium: "Media", strong: "Segura" };
  const strengthColor = { weak: "bg-red-400", medium: "bg-yellow-400", strong: "bg-green-500" };
  const strengthWidth = { weak: "w-1/3", medium: "w-2/3", strong: "w-full" };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    try {
      setLoading(true);
      await API.post(`/auth/reset-password/${token}`, { password });
      setDone(true);
    } catch (err) {
      const msg = err.response?.data?.message;
      setError(msg || "El enlace es inválido o ya expiró. Solicita uno nuevo.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={36} className="text-green-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">¡Contraseña actualizada!</h1>
          <p className="text-sm text-gray-500 mb-6">
            Tu contraseña ha sido restablecida exitosamente. Ya puedes iniciar sesión con tu nueva contraseña.
          </p>
          <button
            onClick={() => navigate("/login")}
            className="w-full py-3 rounded-xl bg-supernova-400 hover:bg-supernova-500 text-black font-bold transition"
          >
            Ir al inicio de sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">

        <div className="w-12 h-12 rounded-full bg-supernova-100 flex items-center justify-center mb-5">
          <Lock size={22} className="text-supernova-500" />
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-1">Nueva contraseña</h1>
        <p className="text-sm text-gray-500 mb-6">
          Crea una contraseña segura para tu cuenta.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nueva contraseña
            </label>
            <div className="relative">
              <input
                type={showPass ? "text" : "password"}
                placeholder="Mínimo 6 caracteres"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 pr-11 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>

            {/* Strength bar */}
            {strength && (
              <div className="mt-2">
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full transition-all ${strengthColor[strength]} ${strengthWidth[strength]}`} />
                </div>
                <p className={`text-xs mt-1 font-medium ${
                  strength === "weak" ? "text-red-500" : strength === "medium" ? "text-yellow-600" : "text-green-600"
                }`}>
                  {strengthLabel[strength]}
                </p>
              </div>
            )}
          </div>

          {/* Confirm password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirmar contraseña
            </label>
            <div className="relative">
              <input
                type={showConfirm ? "text" : "password"}
                placeholder="Repite tu contraseña"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className={`w-full px-4 py-3 pr-11 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:border-transparent ${
                  confirm && password !== confirm
                    ? "border-red-300 focus:ring-red-300"
                    : "border-gray-200 focus:ring-supernova-400"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirm ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
            {confirm && password !== confirm && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <XCircle size={12} /> Las contraseñas no coinciden
              </p>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl bg-supernova-400 hover:bg-supernova-500 text-black font-bold transition flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? (
              <><Loader2 size={16} className="animate-spin" /> Guardando...</>
            ) : (
              "Guardar contraseña"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
